/**
 * ProductAdjustmentView Component (Stufe 5.3)
 *
 * Full-screen product adjustment mode that replaces the CanvasEditor
 * in the main view area. Shows a large product mockup where the user
 * can drag/resize the design placement for a single product.
 *
 * Key Design Decisions:
 * - Uses LOCAL override state (only persisted on "Done")
 * - "Back" discards changes (no auto-save)
 * - Canvas stays mounted but hidden (no state loss)
 * - Only changes placement overrides (Level 2), not master canvas
 * - Live preview via placementFromRelative() from @designdeck/shared
 *
 * Architecture:
 * ┌────────────────────────────────────────────────┐
 * │  ← Back to Canvas    "Product Name"   [front▾] │  Header
 * ├────────────────────────────────────────────────┤
 * │                                                │
 * │        [  Large DraggableMockup  ]             │  Main Area
 * │                                                │
 * ├────────────────────────────────────────────────┤
 * │  [↺ Reset]    Scale ────●────  100%    [Done]  │  Controls
 * └────────────────────────────────────────────────┘
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, RotateCcw, Move } from 'lucide-react';

import { useUIStore } from '@/stores/uiStore';
import { placementAreaOfTemplate } from '@/hooks/useAdaptedPreviews';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { useProducts } from '@/hooks';
import { Slider } from '@/components/ui';
import { cn } from '@/lib/utils';

import {
  areaFromPrintContext,
  carryOverPlacement,
  placementFromRelative,
  type RelativeDesignState,
} from '@designdeck/shared';

import {
  transformProduct,
  findTemplateByPosition,
  getDefaultTemplate,
  getConfiguredPositionTemplates,
} from '@/lib/product-transformer';

import { DraggableMockup } from './DraggableMockup';
import { isEmbroideryProduct } from '@/lib/decoration-method';

// =============================================================================
// TYPES
// =============================================================================

interface ProductAdjustmentViewProps {
  /** Product ID to adjust */
  productId: string;
}

// =============================================================================
// SELECTORS
// =============================================================================

/**
 * Select design state for a specific position from canvasStateManager.
 *
 * Uses a reference-stable selector pattern to avoid infinite re-render loops.
 * The raw PositionState reference from the store is stable (only changes on actual
 * state mutations), so we select it directly and derive fields in useMemo.
 */
function usePositionDesignState(positionSlug: string) {
  // Select the raw PositionState reference (stable, no new object per render)
  const positionState = useCanvasStateManager(
    (state) => state.masterPositions[positionSlug] ?? null
  );

  // Derive the fields we need (only recalculates when positionState reference changes)
  return useMemo(() => {
    if (!positionState?.relativeState) return null;
    return {
      relativeState: positionState.relativeState,
      cropUrl:
        positionState.designCropDataUrl ?? positionState.designCropUrl ?? null,
      printContext: {
        widthPx: positionState.printContext.widthPx,
        heightPx: positionState.printContext.heightPx,
        widthMm: positionState.printContext.widthMm,
        heightMm: positionState.printContext.heightMm,
      },
    };
  }, [positionState]);
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ProductAdjustmentView({
  productId,
}: ProductAdjustmentViewProps) {
  const { t } = useTranslation();
  const setEditorMode = useUIStore((s) => s.setEditorMode);
  const setProductPlacement = useCanvasStateManager(
    (s) => s.setProductPlacement
  );
  const getProductPlacement = useCanvasStateManager(
    (s) => s.getProductPlacement
  );
  // The design's master: rule 1 for a live context without a product id
  const masterProductId = useCanvasStateManager((s) => s.masterProductId);

  // Active position from productSelectionStore
  const activePosition = useProductSelectionStore((s) => s.activePosition);
  const focusedProductId = useProductSelectionStore((s) => s.focusedProductId);
  const focusedColorId = useProductSelectionStore((s) => s.focusedColorId);
  // Selections give the saved selected colors for this specific product
  const productSelection = useProductSelectionStore(
    (s) => s.selections[productId]
  );
  const [adjustPosition, setAdjustPosition] = useState(
    activePosition || 'front'
  );

  // Get product data
  const { data: apiProducts } = useProducts();
  const product = useMemo(() => {
    if (!apiProducts) return null;
    const apiProduct = apiProducts.find((p) => p.id === productId);
    if (!apiProduct) return null;
    return transformProduct(apiProduct);
  }, [productId, apiProducts]);

  // Embroidered products keep the shading base below the design (Shirtigo)
  const isEmbroidery = product ? isEmbroideryProduct(product) : false;

  // Get template for current adjustment position
  const template = useMemo(() => {
    if (!product) return null;
    return (
      findTemplateByPosition(product.mockupTemplates, adjustPosition) ||
      getDefaultTemplate(product.mockupTemplates)
    );
  }, [product, adjustPosition]);

  // Available positions for this product - only the ones it configures
  const availablePositions = useMemo(() => {
    if (!product) return [];
    return getConfiguredPositionTemplates(product.mockupTemplates).map(
      (t) => t.positionKey
    );
  }, [product]);

  // Get design state for current position
  const positionDesignState = usePositionDesignState(adjustPosition);

  // Build auto-adapted placement for current position/product.
  // Same rule as the canvas and the product cards: carry the live state
  // over to this print area (percents, classes, tiers, a deliberate
  // overflow); the product the state was measured on shows it unchanged.
  const autoPlacement = useMemo(() => {
    if (!positionDesignState || !template) return null;

    const target = placementAreaOfTemplate(template);
    if (!target) return null;

    return carryOverPlacement(
      positionDesignState.relativeState,
      areaFromPrintContext(positionDesignState.printContext),
      target,
      { isMaster: productId === masterProductId }
    );
  }, [positionDesignState, template, productId, masterProductId]);

  // Load stored custom placement as initial local state.
  const storedPlacement = useMemo(
    () => getProductPlacement(productId, adjustPosition),
    [productId, adjustPosition, getProductPlacement]
  );

  // Local placement state (only persisted on "Done").
  const [localPlacement, setLocalPlacement] =
    useState<RelativeDesignState | null>(storedPlacement ?? autoPlacement);
  const [isReset, setIsReset] = useState(false);

  // Reset local placement when position changes.
  useEffect(() => {
    const customPlacement = getProductPlacement(productId, adjustPosition);
    setLocalPlacement(customPlacement ?? autoPlacement);
    setIsReset(false);
  }, [productId, adjustPosition, autoPlacement, getProductPlacement]);

  // Get primary color for display — prefer the focused color when it belongs to
  // this product, then fall back to the first selected color from the store.
  const displayColor = useMemo(() => {
    if (!product) return '#FFFFFF';
    // Use focusedColorId only when this product is the focused one
    if (focusedProductId === productId && focusedColorId) {
      const focused = product.colors.find((c) => c.id === focusedColorId);
      if (focused) return focused.hex;
    }
    // Fall back to first selected color from store state (populated by loadFromDesign)
    const selectedIds = productSelection?.selectedColors ?? [];
    const primary = product.colors.find((c) => selectedIds.includes(c.id));
    return primary?.hex || product.colors[0]?.hex || '#FFFFFF';
  }, [product, focusedProductId, productId, focusedColorId, productSelection]);

  const isHeathered = useMemo(() => {
    if (!product) return false;
    if (focusedProductId === productId && focusedColorId) {
      const focused = product.colors.find((c) => c.id === focusedColorId);
      if (focused) return focused.isHeathered ?? false;
    }
    const selectedIds = productSelection?.selectedColors ?? [];
    const primary = product.colors.find((c) => selectedIds.includes(c.id));
    return primary?.isHeathered ?? product.colors[0]?.isHeathered ?? false;
  }, [product, focusedProductId, productId, focusedColorId, productSelection]);

  // Compute live preview placement from local relative placement.
  // The local placement already lives in this product's print area, so it is
  // shown as-is (no fit, no clamp) — the mockup clips to the print area.
  const finalPlacement = useMemo(() => {
    if (!template || !localPlacement) return null;

    const target = {
      width: template.printAreaRect.width,
      height: template.printAreaRect.height,
    };

    if (target.width <= 0 || target.height <= 0) return null;
    return placementFromRelative(localPlacement, target);
  }, [template, localPlacement]);

  // ─────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────

  const handleDone = useCallback(() => {
    if (isReset) {
      setProductPlacement(productId, adjustPosition, null);
    } else if (localPlacement) {
      setProductPlacement(productId, adjustPosition, localPlacement);
    }
    setEditorMode({ type: 'design' });
  }, [
    productId,
    adjustPosition,
    isReset,
    localPlacement,
    setProductPlacement,
    setEditorMode,
  ]);

  const handleBack = useCallback(() => {
    // Discard changes, return to canvas
    setEditorMode({ type: 'design' });
  }, [setEditorMode]);

  const handleReset = useCallback(() => {
    if (!autoPlacement) return;
    setIsReset(true);
    setLocalPlacement(autoPlacement);
  }, [autoPlacement]);

  const handleScaleChange = useCallback(
    (values: number[]) => {
      const scalePercent = values[0];
      if (typeof scalePercent !== 'number' || !localPlacement || !autoPlacement)
        return;
      const factor = scalePercent / 100;
      setIsReset(false);
      setLocalPlacement({
        ...localPlacement,
        relWidth: Math.max(0.02, autoPlacement.relWidth * factor),
      });
    },
    [localPlacement, autoPlacement]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleBack();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleDone();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleBack, handleDone]);

  const hasCustomPlacement = Boolean(storedPlacement);
  const hasLocalDeviation = useMemo(() => {
    if (!localPlacement || !autoPlacement) return false;
    const eps = 1e-6;
    return (
      Math.abs(localPlacement.relCenterX - autoPlacement.relCenterX) > eps ||
      Math.abs(localPlacement.relCenterY - autoPlacement.relCenterY) > eps ||
      Math.abs(localPlacement.relWidth - autoPlacement.relWidth) > eps
    );
  }, [localPlacement, autoPlacement]);
  const canReset = hasCustomPlacement || hasLocalDeviation;

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────

  if (!product || !template) {
    return (
      <div className="flex-1 flex items-center justify-center text-mute">
        {t('creator.adjust.productNotFound')}
      </div>
    );
  }

  const scalePercent =
    localPlacement && autoPlacement && autoPlacement.relWidth > 0
      ? Math.round((localPlacement.relWidth / autoPlacement.relWidth) * 100)
      : 100;

  return (
    <div className="flex flex-col h-full bg-[rgb(var(--color-canvas-bg))]">
      {/* ═══════════════════════════════════════════════════════════════
          HEADER BAR
          ═══════════════════════════════════════════════════════════════ */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-paper/90 backdrop-blur-sm border-b border-line z-10">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink transition-colors"
          title={t('creator.adjust.backToCanvasTitle')}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('creator.adjust.backToCanvas')}</span>
        </button>

        {/* Product Name + Mode Indicator */}
        <div className="flex items-center gap-2">
          <Move className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-ink">
            {t('creator.adjust.title', { name: product.name })}
          </span>
          {/* Color swatch */}
          <div
            className="w-4 h-4 rounded-full border border-line"
            style={{ backgroundColor: displayColor }}
          />
        </div>

        {/* Position Selector */}
        <div className="flex items-center gap-1">
          {availablePositions.map((pos) => (
            <button
              key={pos}
              onClick={() => setAdjustPosition(pos)}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-control transition-colors capitalize',
                adjustPosition === pos
                  ? 'bg-primary text-primary-fg'
                  : 'bg-paper-2 text-ink-2 hover:bg-paper-3'
              )}
            >
              {pos === 'front'
                ? t('creator.positions.front')
                : pos === 'back'
                  ? t('creator.positions.back')
                  : pos}
            </button>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MAIN MOCKUP AREA
          ═══════════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex items-center justify-center p-8 min-h-0">
        {positionDesignState ? (
          <DraggableMockup
            template={template}
            backgroundColor={displayColor}
            isHeathered={isHeathered}
            isEmbroidery={isEmbroidery}
            placement={finalPlacement}
            designCropUrl={positionDesignState.cropUrl}
            placementState={
              localPlacement ??
              autoPlacement ??
              positionDesignState.relativeState
            }
            onPlacementChange={(nextPlacement) => {
              setIsReset(false);
              setLocalPlacement(nextPlacement);
            }}
          />
        ) : (
          <div className="text-center text-mute">
            <p className="text-sm">{t('creator.adjust.noDesign')}</p>
            <p className="text-xs mt-1">{t('creator.adjust.noDesignHint')}</p>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          BOTTOM CONTROLS BAR
          ═══════════════════════════════════════════════════════════════ */}
      <div className="flex items-center justify-between px-6 py-3 bg-paper/90 backdrop-blur-sm border-t border-line z-10">
        {/* Reset Button */}
        <button
          onClick={handleReset}
          disabled={!canReset}
          className={cn(
            'flex items-center gap-1.5 text-sm transition-colors',
            canReset
              ? 'text-ink-2 hover:text-ink'
              : 'text-ink-3 cursor-not-allowed'
          )}
          title={t('creator.adjust.resetTitle')}
        >
          <RotateCcw className="w-4 h-4" />
          <span>{t('creator.adjust.reset')}</span>
        </button>

        {/* Scale Slider */}
        <div className="flex items-center gap-3">
          <label className="text-xs text-mute">
            {t('creator.adjust.scale')}
          </label>
          <Slider
            min={50}
            max={200}
            step={1}
            value={[scalePercent]}
            onValueChange={handleScaleChange}
            className="w-36"
          />
          <span className="text-sm font-medium text-ink-2 tabular-nums w-12 text-right">
            {scalePercent}%
          </span>
        </div>

        {/* Done Button */}
        <button
          onClick={handleDone}
          className="px-4 py-1.5 bg-primary text-primary-fg text-sm font-medium rounded-control hover:brightness-[0.92] transition-colors "
          title={t('creator.adjust.doneTitle')}
        >
          {t('creator.adjust.done')}
        </button>
      </div>
    </div>
  );
}

export default ProductAdjustmentView;
